import { useCallback, useEffect, useRef, useState } from "react";
import type { SurfaceViewPreference } from "@shared/chatPreferences";
import { mergeSurfacePreference } from "@shared/chatPreferences";
import { useChatPreferences } from "@/hooks/useChatPreferences";

export type ViewMode = "list" | "grid" | "table" | "card";
export type SortDirection = "asc" | "desc";

export interface ViewPreferences<TSortField extends string = string> {
  viewMode: ViewMode;
  sortField: TSortField;
  sortDirection: SortDirection;
}

interface UseViewPreferencesOptions<TSortField extends string> {
  storageKey: string;
  defaultViewMode?: ViewMode;
  defaultSortField: TSortField;
  defaultSortDirection?: SortDirection;
}

const VIEW_MODES: readonly string[] = ["list", "grid", "table", "card"];

function readLegacy(fullKey: string, suffix: string): string | null {
  try {
    if (typeof window === "undefined") return null;
    return localStorage.getItem(`${fullKey}.${suffix}`);
  } catch {
    return null;
  }
}

function removeLegacy(fullKey: string): void {
  try {
    if (typeof window === "undefined") return;
    localStorage.removeItem(`${fullKey}.viewMode`);
    localStorage.removeItem(`${fullKey}.sortField`);
    localStorage.removeItem(`${fullKey}.sortDirection`);
  } catch {
    // best-effort
  }
}

/**
 * DS EDIT 8 — per-surface presentation preferences, account-synced.
 *
 * Same external API as before, new persistence order:
 * 1. Legacy localStorage (`deeply.<key>.*`) seeds initial render — no flicker.
 * 2. Once the synced store hydrates: an explicit server entry for this surface
 *    WINS (and the legacy keys are removed — migration confirmed persisted).
 *    If the server has no entry but legacy values exist, they are imported
 *    once (idempotent: import writes the entry, so it never re-runs).
 * 3. Every change writes through the store (optimistic + debounced PATCH) —
 *    refresh, sign-out/in, and other devices all see the choice.
 * Signed-out sessions keep working on localStorage alone.
 */
export function useViewPreferences<TSortField extends string>({
  storageKey,
  defaultViewMode = "list",
  defaultSortField,
  defaultSortDirection = "desc",
}: UseViewPreferencesOptions<TSortField>) {
  const fullKey = `deeply.${storageKey}`;
  const { preferences, isHydrated, updatePreference } = useChatPreferences();
  const serverEntry: SurfaceViewPreference | undefined =
    preferences.uiPreferences.surfaces[storageKey];

  const [viewMode, setViewModeState] = useState<ViewMode>(() => {
    const stored = readLegacy(fullKey, "viewMode");
    return stored && VIEW_MODES.includes(stored) ? (stored as ViewMode) : defaultViewMode;
  });
  const [sortField, setSortFieldState] = useState<TSortField>(() => {
    return (readLegacy(fullKey, "sortField") as TSortField) || defaultSortField;
  });
  const [sortDirection, setSortDirectionState] = useState<SortDirection>(() => {
    const stored = readLegacy(fullKey, "sortDirection");
    return stored === "asc" || stored === "desc" ? stored : defaultSortDirection;
  });

  const writeToStore = useCallback(
    (partial: SurfaceViewPreference) => {
      updatePreference(
        "uiPreferences",
        mergeSurfacePreference(preferences.uiPreferences, storageKey, partial)
      );
    },
    [updatePreference, preferences.uiPreferences, storageKey]
  );

  // Reconcile once per hydration: server entry wins; otherwise import legacy.
  const reconciledRef = useRef(false);
  useEffect(() => {
    if (!isHydrated || reconciledRef.current) return;
    reconciledRef.current = true;
    if (serverEntry) {
      if (serverEntry.viewMode && VIEW_MODES.includes(serverEntry.viewMode)) {
        setViewModeState(serverEntry.viewMode as ViewMode);
      }
      if (serverEntry.sortField) setSortFieldState(serverEntry.sortField as TSortField);
      if (serverEntry.sortDirection) setSortDirectionState(serverEntry.sortDirection);
      removeLegacy(fullKey); // server copy confirmed — legacy keys retired
      return;
    }
    // No server entry: import current (legacy-seeded) values once if any
    // legacy key actually existed — defaults alone are not worth persisting.
    const hadLegacy =
      readLegacy(fullKey, "viewMode") !== null ||
      readLegacy(fullKey, "sortField") !== null ||
      readLegacy(fullKey, "sortDirection") !== null;
    if (hadLegacy) {
      writeToStore({ viewMode, sortField, sortDirection });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot reconcile on hydration
  }, [isHydrated]);

  const setViewMode = useCallback(
    (mode: ViewMode) => {
      setViewModeState(mode);
      try {
        if (typeof window !== "undefined") localStorage.setItem(`${fullKey}.viewMode`, mode);
      } catch {}
      writeToStore({ viewMode: mode });
    },
    [fullKey, writeToStore]
  );

  const setSortField = useCallback(
    (field: TSortField) => {
      setSortFieldState(field);
      try {
        if (typeof window !== "undefined") localStorage.setItem(`${fullKey}.sortField`, field);
      } catch {}
      writeToStore({ sortField: field });
    },
    [fullKey, writeToStore]
  );

  const setSortDirection = useCallback(
    (direction: SortDirection) => {
      setSortDirectionState(direction);
      try {
        if (typeof window !== "undefined") localStorage.setItem(`${fullKey}.sortDirection`, direction);
      } catch {}
      writeToStore({ sortDirection: direction });
    },
    [fullKey, writeToStore]
  );

  const toggleSortDirection = useCallback(() => {
    const newDirection = sortDirection === "asc" ? "desc" : "asc";
    setSortDirection(newDirection);
  }, [sortDirection, setSortDirection]);

  const setSort = useCallback(
    (field: TSortField, direction: SortDirection) => {
      setSortFieldState(field);
      setSortDirectionState(direction);
      try {
        if (typeof window !== "undefined") {
          localStorage.setItem(`${fullKey}.sortField`, field);
          localStorage.setItem(`${fullKey}.sortDirection`, direction);
        }
      } catch {}
      writeToStore({ sortField: field, sortDirection: direction });
    },
    [fullKey, writeToStore]
  );

  return {
    viewMode,
    setViewMode,
    sortField,
    setSortField,
    sortDirection,
    setSortDirection,
    toggleSortDirection,
    setSort,
  };
}
